import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { DNABox } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import DNASlideRoll from 'src/components/DNA/SlideRoll/DNASlideRoll';
import { SLIDE_ROLL_VARIANT } from 'src/components/DNA/SlideRoll/StyleSetting';
import useBreakpointValue, { BREAKPOINT_CONFIG } from 'src/hooks/useBreakpointValue';
import { useContent } from 'src/state/context/ContentProvider/ContentProvider.proxy';
import { LoadedPresentation, PresentableModelORM } from 'src/state/context/ContentProvider/ContentProvider';
import useHiddenSlideToggle from 'src/hooks/useHiddenSlideToggle';
import { isCustomDeckORM, isFolderItemORM } from 'src/types/typeguards';

const styles = StyleSheet.create({
  mainWrapper: {
    backgroundColor: colors['color-black'],
    paddingHorizontal: 14,
    paddingVertical: 14,
  },
});

interface Props {
  presentation: LoadedPresentation,
  onPageSelect: (presentationPageNumber: number) => void,
  showCloseButton?: boolean,
  closeButtonOnPress?: () => void,
  addPresentation?: (item: PresentableModelORM, pageNumber?: number) => void
}

const SlideRollWrapper = (props: Props) => {
  const { presentation, onPageSelect, closeButtonOnPress, addPresentation } = props;
  const responsiveBreakpoint = useBreakpointValue('meetingSlideRoll');
  const { updateHiddenSlideState } = useContent();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const isCustomDeck = isFolderItemORM(presentation.presentable.orm) &&
    isCustomDeckORM(presentation.presentable.orm.relations.itemORM);
  const onToggleHidden = useHiddenSlideToggle({
    folderItemORM: isFolderItemORM(presentation.presentable.orm)
      ? presentation.presentable.orm
      : undefined,
  });

  const memoizedSlideRoll = useMemo(() => (
    <DNASlideRoll
      presentable={presentation.presentable}
      activeSlide={presentation.currentPresentablePage}
      variant={SLIDE_ROLL_VARIANT.MEETINGS}
      addPresentation={addPresentation}
      onSelect={onPageSelect}
      onToggleHidden={isCustomDeck ? undefined : onToggleHidden}
      onHiddenStateChange={updateHiddenSlideState}
      displayGroupSelection
    />
  ), [addPresentation, isCustomDeck, onPageSelect, onToggleHidden, presentation, updateHiddenSlideState]);

  const slideRollContainerWidth = useMemo(
    () => {
      const targetWidth = isExpanded
        ? responsiveBreakpoint
        : BREAKPOINT_CONFIG.meetingSlideRoll.values.sm

      return {
        ...styles.mainWrapper,
        minWidth: targetWidth,
        width: targetWidth,
      }
    },
    [responsiveBreakpoint, isExpanded],
  )

  const toggleExpanded = useCallback(
    () => { setIsExpanded(p => !p) },
    [setIsExpanded],
  )

  return (
    <DNABox fill appearance="col" style={slideRollContainerWidth}>
      <DNASlideRoll.HeaderActionButtons
        onClose={closeButtonOnPress}
        displayExpandBtn
        isExpanded={isExpanded}
        onToggleSlideRollWidth={toggleExpanded}
      />
      {memoizedSlideRoll}
    </DNABox>
  );
};

const SlideRollConnector = (props) => {
  const { activePresentation, setActiveSlideByPresentationPageNumber, addPresentation } = useContent();
  return (
    <SlideRollWrapper
      presentation={activePresentation!}
      onPageSelect={setActiveSlideByPresentationPageNumber}
      closeButtonOnPress={props.closeButtonOnPress}
      addPresentation={addPresentation}
    />
  );
};

SlideRollWrapper.title = 'SlideRollWrapper';

export default SlideRollConnector;
